import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../services/api";
import { API_URLS } from "../../services/configUrls";
import salesRibbonImg from "../../assets/imgs/Isolated_Academic_Team_with_Desk_Props.png";

// Allowed roles for the SalesReady banner
const ALLOWED_ROLES = [
  "spoc",
  "dspoc",
  "dspc",
  "leaders",
  "leader",
  "educator",
  "educators",
  "educatord",
];

const SalesReadyBanner = () => {
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === "dark";
  const userRoleFromState = useSelector((state) => state.authorise.userRole);

  const rawRole =
    userRoleFromState ||
    localStorage.getItem("userRole") ||
    localStorage.getItem("role") ||
    "";
  const normalizedRole = rawRole.toString().toLowerCase().trim();

  if (!ALLOWED_ROLES.includes(normalizedRole)) {
    return null;
  }

  const handleBannerClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await api.get(API_URLS.INDUSTRY_PROGRAM_REDIRECT);
      const redirectUrl =
        typeof response.data === "string"
          ? response.data
          : response.data?.redirect_url || response.data?.url || response.data?.data;

      if (redirectUrl) {
        const newWindow = window.open(redirectUrl, "_blank", "noopener,noreferrer");
        if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
          window.location.href = redirectUrl;
        }
      } else {
        toast.error("Could not obtain redirect URL. Please try again.");
      }
    } catch (error) {
      console.error("Error during SalesReady redirect:", error);
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to redirect to industry program. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleBannerClick();
    }
  };

  // ─── Feature pills data ──────────────────────────────────────
  const features = [
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="18" rx="3" stroke="#d97706" strokeWidth="2" />
          <circle cx="12" cy="10" r="3" stroke="#d97706" strokeWidth="2" />
          <path d="M6 19c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#d97706" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
      label: "6 Live Sessions",
      sub: "Interactive & practical learning",
    },
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="9" cy="7" r="4" stroke="#d97706" strokeWidth="2" />
          <path d="M2 21v-2a5 5 0 015-5h4a5 5 0 015 5v2" stroke="#d97706" strokeWidth="2" strokeLinecap="round" />
          <circle cx="18" cy="8" r="3" stroke="#d97706" strokeWidth="1.5" />
          <path d="M18 11a4 4 0 014 4v1" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
      label: "Manoj Chugh & Team",
      sub: "Learn from industry experts",
    },
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="#ef4444" strokeWidth="2" />
          <path d="M16 2v4M8 2v4M3 10h18" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
      label: "Batch: Coming soon",
      sub: "Mark your calendar & get ready",
      isRed: true,
    },
  ];

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="SalesReady Certification Program - Explore SalesReady"
      onClick={handleBannerClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        width: "100%",
        mb: 2.5,
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: loading ? "wait" : "pointer",
        transition: "all 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
        background: isDark
          ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)"
          : "linear-gradient(135deg, #fffdf5 0%, #fff9e6 35%, #fef3c7 70%, #f7d683 100%)",
        boxShadow: hovered
          ? "0 12px 32px rgba(217, 119, 6, 0.18)"
          : "0 2px 12px rgba(0, 0, 0, 0.06)",
        border: isDark ? "1px solid rgba(251, 191, 36, 0.25)" : "1px solid rgba(217, 119, 6, 0.15)",
        transform: hovered && !loading ? "translateY(-2px)" : "translateY(0)",
        "&:focus-visible": {
          outline: "2px solid #f59e0b",
          outlineOffset: "2px",
        },
      }}
    >
      {/* ─── Main Content Row ─────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: { xs: "auto", md: "130px" },
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* ─── LEFT: Text Content ─────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            px: { xs: 2.5, md: 4 },
            py: { xs: 2, md: 2 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1,
            zIndex: 2,
          }}
        >
          {/* Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              backgroundColor: isDark ? "rgba(251, 191, 36, 0.15)" : "rgba(234, 179, 8, 0.14)",
              border: isDark ? "1px solid rgba(251, 191, 36, 0.3)" : "1px solid rgba(234, 179, 8, 0.28)",
              borderRadius: "20px",
              px: 1.2,
              py: 0.25,
              width: "fit-content",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                boxShadow: "0 0 6px rgba(34, 197, 94, 0.6)",
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                color: isDark ? "#fbbf24" : "#92400e",
                letterSpacing: "0.3px",
                lineHeight: 1,
              }}
            >
              📣 Dear Faculty Members
            </Typography>
          </Box>

          {/* Heading */}
          <Typography
            sx={{
              fontSize: { xs: "15px", sm: "17px", md: "19px" },
              fontWeight: 800,
              color: isDark ? "#f1f5f9" : "#1e293b",
              lineHeight: 1.25,
              mt: 0.1,
            }}
          >
            Learning never stops! Join the upcoming{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                display: "inline",
              }}
            >
              SalesReady Certification Program
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              fontSize: { xs: "11px", md: "12px" },
              color: isDark ? "#cbd5e1" : "#475569",
              fontWeight: 500,
              lineHeight: 1.35,
              mt: -0.2,
            }}
          >
            Enhance your professional skills to career readiness through industry-lead live training.
          </Typography>

          {/* Tagline Motto */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              backgroundColor: isDark ? "rgba(251, 191, 36, 0.08)" : "rgba(245, 158, 11, 0.1)",
              border: isDark ? "1px solid rgba(251, 191, 36, 0.22)" : "1px solid rgba(217, 119, 6, 0.18)",
              borderRadius: "8px",
              px: 1.2,
              py: 0.3,
              width: "fit-content",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "10px", md: "11px" },
                fontWeight: 600,
                color: isDark ? "#fbbf24" : "#92400e",
                letterSpacing: "0.2px",
              }}
            >
              Upgrade your skills. Empower your students. Shape future professionals! 🚀
            </Typography>
          </Box>

          {/* Feature Pills */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              mt: 0.4,
            }}
          >
            {features.map((f, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.7,
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "#ffffff",
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.1)"
                    : f.isRed
                    ? "1px solid rgba(239, 68, 68, 0.2)"
                    : "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "8px",
                  px: 1,
                  py: 0.4,
                  transition: "all 0.2s",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "6px",
                    backgroundColor: isDark
                      ? "rgba(251,191,36,0.12)"
                      : f.isRed
                      ? "rgba(239,68,68,0.08)"
                      : "rgba(245,158,11,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      color: isDark ? "#e2e8f0" : f.isRed ? "#dc2626" : "#1e293b",
                      lineHeight: 1.15,
                    }}
                  >
                    {f.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontWeight: 400,
                      color: isDark ? "#64748b" : "#94a3b8",
                      lineHeight: 1.2,
                    }}
                  >
                    {f.sub}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ─── CENTER: Illustration ───────────────────────────── */}
        <Box
          sx={{
            width: { xs: "180px", sm: "220px", md: "280px" },
            flexShrink: 0,
            display: { xs: "none", sm: "flex" },
            alignItems: "flex-end",
            justifyContent: "center",
            position: "relative",
            overflow: "visible",
            zIndex: 1,
          }}
        >
          {/* Soft glow behind illustration */}
          <Box
            sx={{
              position: "absolute",
              width: "80%",
              height: "80%",
              bottom: "10%",
              borderRadius: "50%",
              background: isDark
                ? "radial-gradient(ellipse, rgba(251,191,36,0.12) 0%, transparent 70%)"
                : "radial-gradient(ellipse, rgba(251,191,36,0.18) 0%, transparent 70%)",
              filter: "blur(15px)",
            }}
          />
          <Box
            component="img"
            src={salesRibbonImg}
            alt="SalesReady Illustration"
            sx={{
              width: "100%",
              height: "auto",
              maxHeight: "150px",
              objectFit: "contain",
              userSelect: "none",
              pointerEvents: "none",
              position: "relative",
              zIndex: 2,
              filter: isDark ? "brightness(0.95)" : "none",
            }}
          />
        </Box>

        {/* ─── RIGHT: CTA Button ──────────────────────────────── */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 0 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: isDark ? "#fbbf24" : "#1e293b",
              color: isDark ? "#1e293b" : "#ffffff",
              borderRadius: "12px",
              px: { xs: 2, md: 2.5 },
              py: { xs: 0.9, md: 1.1 },
              fontWeight: 700,
              fontSize: { xs: "11px", md: "12px" },
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              transition: "all 0.25s ease",
              boxShadow: hovered
                ? isDark
                  ? "0 4px 16px rgba(251, 191, 36, 0.4)"
                  : "0 4px 16px rgba(30, 41, 59, 0.35)"
                : "0 2px 8px rgba(0,0,0,0.15)",
              transform: hovered ? "scale(1.04)" : "scale(1)",
            }}
          >
            EXPLORE SALESREADY
            {/* Arrow circle */}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: isDark ? "rgba(30,41,59,0.2)" : "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.25s ease",
                transform: hovered ? "translateX(3px)" : "translateX(0)",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─── Loading Overlay ──────────────────────────────────── */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            backgroundColor: "rgba(17, 24, 39, 0.7)",
            backdropFilter: "blur(6px)",
            zIndex: 20,
            borderRadius: "16px",
          }}
        >
          <CircularProgress
            size={36}
            thickness={4}
            sx={{ color: "#fbbf24" }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              color: "#ffffff",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textShadow: "0 2px 6px rgba(0,0,0,0.5)",
            }}
          >
            Connecting to SalesReady Program...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SalesReadyBanner;
